import {
  appendChannelMessage,
  flattenRawMessage,
  updateMessage,
} from "./messages";
import {
  flattenRawUser,
  removeUserTypingByChannel,
  updateUser,
  upsertUserTypingByChannel,
} from "./users";
import { getClient, getSettings } from "../client";
import { DM_TYPE, ROOM_TYPE, channelOptsEverything } from "../constants";
import { createRoomsFilterByUser } from "../hooks/utils";
import {
  channelsByChannelId,
  connectedAsUser,
  isOnline,
  messagesByChannelId,
  rawChannelsById,
  totalUnreadChannelCount,
  totalUnreadCount,
  usersTypingByChannelId,
} from "../state";

export function flattenRawChannel(rawChannel) {
  const { id, type, data, state } = rawChannel;
  const { description, hostUserIds, memberLeaderId, publisherId } = data;
  const { messages } = state;
  let { name, image } = data;
  const { id: userId } = connectedAsUser.get();
  const membersObj = (state && state.members) || {};
  const memberUserIds = Object.keys(membersObj);
  if (type === DM_TYPE) {
    image = null;
    const otherMembers = memberUserIds.reduce((accum, uId) => {
      if (uId !== userId) {
        accum.push(membersObj[uId].user);
      }
      return accum;
    }, []);
    otherMembers.sort((a, b) => (a.name > b.name ? 1 : -1));
    const firstUser = otherMembers[0];
    image = firstUser.avatar;
    name = otherMembers.map((m) => m.name).join(", ");
  }
  const lastMessage =
    (messages.length && flattenRawMessage(messages[messages.length - 1])) || {};
  const typingUsers = usersTypingByChannelId.get();
  // rawChannel has an isTyping flag but it is contextualized for if my current user is typing
  const isTyping = Boolean(typingUsers[id] && typingUsers[id].length);

  return {
    channelId: id,
    name,
    image,
    description,
    memberCount: data.member_count,
    type,
    isMember: !!membersObj[userId],
    isMuted: rawChannel.muteStatus().muted,
    memberUserIds,
    hostUserIds,
    unreadCount: (state && state.unreadCount) || 0,
    lastReadAt:
      (state &&
        state.read &&
        state.read[userId] &&
        state.read[userId].last_read) ||
      null,
    isTyping,
    lastMessage,
    memberLeaderId,
    publisherId,
  };
}

export function hasChannelChanged(oldChannel, newChannel) {
  return Object.keys(oldChannel).some((prop) => {
    const a = oldChannel[prop];
    const b = newChannel[prop];
    if (prop === "memberUserIds" || prop === "hostUserIds") {
      const aIsArray = Array.isArray(a);
      const bIsArray = Array.isArray(b);
      if (aIsArray && bIsArray) {
        if (a.length !== b.length) {
          return true;
        }
        return a.some((ai, i) => ai !== b[i]);
      }
      return aIsArray !== bIsArray;
    }
    return a !== b;
  });
}

export function upsertChannel(channel) {
  const obj = channelsByChannelId.get();
  const existing = obj[channel.channelId];
  if (!existing || hasChannelChanged(existing, channel)) {
    channelsByChannelId.set({ ...obj, [channel.channelId]: channel });
  }
}

export async function updateLoadedChannel(channelId, { unwatch } = {}) {
  const entry = rawChannelsById.get(channelId);
  if (entry) {
    const channel = flattenRawChannel(entry.rawChannel);
    upsertChannel(channel);
    if (unwatch) {
      await entry.rawChannel.stopWatching();
      entry.watch = false;
      entry.state = false;
      entry.presence = false;
    }
  }
}

export function handleChannelEvent(event) {
  const { logEvents } = getSettings();
  if (logEvents) {
    // eslint-disable-next-line
    console.log(`Channel event ${event.type}`, event);
  }
  if (Number.isFinite(event.total_unread_count)) {
    totalUnreadCount.set(event.total_unread_count);
  }
  if (Number.isFinite(event.unread_channels)) {
    totalUnreadChannelCount.set(event.unread_channels);
  }
  switch (event.type) {
    case "message.new":
      appendChannelMessage(event.channel_id, flattenRawMessage(event.message));
      updateLoadedChannel(event.channel_id);
      break;
    case "reaction.deleted":
    case "reaction.new":
      updateMessage(event.channel_id, flattenRawMessage(event.message));
      break;
    case "member.added":
    case "member.removed":
    case "message.read":
    case "notification.mark_read":
      updateLoadedChannel(event.channel_id);
      break;
    default:
      break;
  }
}

function setChannelMessages(channelId, messages) {
  messagesByChannelId.set((obj) => ({
    ...obj,
    [channelId]: messages,
  }));
}

// TODO: Clean this up, this is the one function with the most cross dependencies since it has to map through everything from a channel
export function registerRawChannel(
  rawChannel,
  { watch, state, presence },
  skipStateSet
) {
  let entry = rawChannelsById.get(rawChannel.id);
  if (!entry) {
    entry = { rawChannel, bound: false, watch, state, presence };
    rawChannelsById.set(rawChannel.id, entry);
  } else {
    if (watch) {
      entry.watch = watch;
    }
    if (state) {
      entry.state = state;
    }
    if (presence) {
      entry.presence = presence;
    }
  }
  if (!entry.bound) {
    // begin listening for events
    entry.rawChannel.on(handleChannelEvent);
    entry.bound = true;
  }
  if (state && rawChannel.state && rawChannel.state.messages) {
    // initialize channel messages from state
    const messages = rawChannel.state.messages.map(flattenRawMessage);
    setChannelMessages(rawChannel.id, messages);
  }
  if (presence && rawChannel.state && rawChannel.state.members) {
    // pre-populate user presence data
    const membersObj = rawChannel.state.members;
    Object.keys(membersObj).forEach((uId) => {
      const member = membersObj[uId];
      const user = flattenRawUser(member.user);
      updateUser(user);
    });
  }
  if (!skipStateSet) {
    const channel = flattenRawChannel(rawChannel);
    upsertChannel(channel);
  }
}

function initExistingChannel({ type, channelId, memberUserIds }) {
  if (type === DM_TYPE && memberUserIds) {
    return getClient().channel(type, { members: memberUserIds });
  }
  if (type === ROOM_TYPE && channelId) {
    return getClient().channel(type, channelId);
  }
  // TODO: might be an issue
  return null;
}

async function checkIfUserIsInChannel(rawChannel) {
  const { id: userId } = connectedAsUser.get();
  if (rawChannel.state.members[userId]) {
    return;
  }
  if (Object.keys(rawChannel.state.members).length < 99) {
    return; // below 100 limit, no extra query needed
  }
  const { members } = await rawChannel.queryMembers({ user_id: userId });
  if (Array.isArray(members) && members.length > 0) {
    const [user] = members;
    rawChannel.state.members[userId] = user;
  }
}

export async function upsertChannelFromEvent(event) {
  const { id: channelId, type, members } = event.channel;
  let rawChannel;
  const entry = rawChannelsById.get(channelId);
  if (entry) {
    if (!entry.watch || !entry.state || !entry.presence) {
      // ensure existing channel is being watched
      rawChannel = entry.rawChannel;
    }
  } else {
    // start watching channel not yet registered
    const memberUserIds =
      type === DM_TYPE ? members.map((m) => m.user_id) : undefined;
    rawChannel = initExistingChannel({ type, channelId, memberUserIds });
  }
  if (rawChannel) {
    const channelOpts = channelOptsEverything;
    await rawChannel.watch({ state: true, presence: true });
    await checkIfUserIsInChannel(rawChannel);
    registerRawChannel(rawChannel, channelOpts);
  } else if (entry) {
    const channel = flattenRawChannel(entry.rawChannel);
    upsertChannel(channel);
  }
}

export function upsertChannels(channels) {
  const obj = channelsByChannelId.get();
  let hasChanged = false;
  const copy = { ...obj };
  channels.forEach((channel) => {
    const existing = obj[channel.channelId];
    if (!existing || hasChannelChanged(existing, channel)) {
      hasChanged = true;
      copy[channel.channelId] = channel;
    }
  });
  if (hasChanged) {
    channelsByChannelId.set(copy);
  }
}

export async function updateChannelMutesFromEvent(event) {
  const currentMutes = (event && event.me && event.me.channel_mutes) || [];
  const currentMuteIds = currentMutes.map((i) => i.channel.id);
  const channelCache = channelsByChannelId.get();

  const channels = Object.keys(channelCache).reduce((accum, channelId) => {
    const cacheItem = channelCache[channelId];
    const shouldBeMuted = currentMuteIds.includes(channelId);

    if (
      (cacheItem.isMuted && !shouldBeMuted) || // remove muted status
      (!cacheItem.isMuted && shouldBeMuted) // add muted status
    ) {
      const entry = rawChannelsById.get(channelId);
      const channel = flattenRawChannel(entry.rawChannel);
      accum.push(channel);
    }
    return accum;
  }, []);

  upsertChannels(channels);
}

export async function removeChannelFromEvent(event) {
  const { id: channelId } = event.channel;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [channelId]: deleted, ...rest } = channelsByChannelId.get();

  // remove the flattened channel, we have to leave the raw channel since that is where we handle "message.new"
  channelsByChannelId.set(rest); // remove flattened channel
}

// Process raw channels to channels
export function getChannelsFromRawChannels(rawChannels, channelOpts) {
  return Promise.all(
    rawChannels.map(async (rawChannel) => {
      // We want to load all unread messages so we will be able to scroll to them
      // but only if there are more then the current message size in state
      if (rawChannel.state.unreadCount > rawChannel.state.messages.length) {
        // the query will keep them on the channel and update the messages list for free
        await rawChannel.query({
          messages: {
            limit: rawChannel.state.unreadCount, // This will pull in all unread messages on top of the default limit
            id_lt: rawChannel.state.messages[0].id,
          },
        });
      }
      registerRawChannel(rawChannel, channelOpts, true);
      return flattenRawChannel(rawChannel);
    })
  );
}

// The initial load of channel type dms
export async function loadChannelDMs() {
  const { id: userId, community } = connectedAsUser.get();
  if (!userId) {
    throw new Error("No currently connected user");
  }
  const filter = {
    type: "messaging",
    members: { $in: [userId] },
    community,
  };
  const sort = [{ last_message_at: -1 }];
  const channelOpts = channelOptsEverything;
  const rawChannels = await getClient().queryChannels(
    filter,
    sort,
    channelOpts
  );

  const channels = await getChannelsFromRawChannels(rawChannels, channelOpts);
  upsertChannels(channels);
  return channels;
}

// The subsequent load of channel type dms for pagination
export async function loadMoreChannelDMs(offset) {
  const { id: userId, community } = connectedAsUser.get();
  if (!userId) {
    throw new Error("No currently connected user");
  }

  const filter = {
    type: DM_TYPE,
    members: { $in: [userId] },
    community,
  };
  const sort = [{ last_message_at: -1 }];
  const options = {
    ...channelOptsEverything,
    offset,
    // Add one more channel than we need to check if there are more channels
    limit: channelOptsEverything.limit + 1,
  };

  const rawChannels = await getClient().queryChannels(filter, sort, options);
  const channels = await getChannelsFromRawChannels(rawChannels, options);
  const hasMore = channels.length > options.limit;

  // Remove the extra channel we added to check if there are more channels
  if (hasMore) {
    channels.pop();
  }

  upsertChannels(channels);
  return {
    hasMore,
    channels,
  };
}

// The initial load of channel type rooms
export async function loadChannelRooms() {
  const user = connectedAsUser.get();
  if (!user) {
    throw new Error("No currently connected user");
  }
  const filter = createRoomsFilterByUser(user);
  const sort = [{ name: 1 }];
  const channelOpts = channelOptsEverything;
  const rawChannels = await getClient().queryChannels(filter, sort, {
    ...channelOpts,
    limit: 20,
  });
  await Promise.all(
    rawChannels.map((rawChannel) => checkIfUserIsInChannel(rawChannel))
  );
  const channels = await getChannelsFromRawChannels(rawChannels, channelOpts);
  upsertChannels(channels);
  return channels;
}

// Refresh the initial set of rooms and then any rooms in memory that didnt get refreshed
export async function refreshChannels() {
  // fresh load of dms and rooms
  const [dms, rooms] = await Promise.all([
    loadChannelDMs(),
    loadChannelRooms(),
  ]);

  // Check for any older channels that didnt get an update
  const channelCache = channelsByChannelId.get();
  const channelIds = new Set(Object.keys(channelCache));
  [...dms, ...rooms].forEach(({ type, channelId }) => {
    // remove from our pending list
    if (type === DM_TYPE || type === ROOM_TYPE) {
      channelIds.delete(channelId);
    }
  });

  // if the channels exist but didnt get refreshed it needs refreshed
  // ie. you have 11 dms in memory and the query only pulls in 10
  // but you have new messages on all 11 so we'll update that 11th
  if (channelIds.size > 0) {
    const filter = {
      id: { $in: [...channelIds.values()] },
    };
    const sort = [{ last_message_at: -1 }];
    const channelOpts = channelOptsEverything;
    const rawChannels = await getClient().queryChannels(
      filter,
      sort,
      channelOpts
    );
    const channels = await getChannelsFromRawChannels(rawChannels, channelOpts);
    upsertChannels(channels);
  }
}

// TODO: I'd prefer to move these event handlers, but there is a circular dependency
export async function handleClientEvent(event) {
  const { logEvents } = getSettings();
  if (event.type !== "health.check" && logEvents) {
    // eslint-disable-next-line
    console.log(`Client event ${event.type}`, event);
  }
  if (Number.isFinite(event.total_unread_count)) {
    totalUnreadCount.set(event.total_unread_count);
  }
  if (Number.isFinite(event.unread_channels)) {
    totalUnreadChannelCount.set(event.unread_channels);
  }
  switch (event.type) {
    case "connection.changed":
      isOnline.set(event.online);
      break;
    case "connection.recovered":
      refreshChannels();
      break;
    case "notification.message_new": // a new message came through on a channel, if previously hidden this shows it
    case "notification.added_to_channel": // current user was added to a channel
      upsertChannelFromEvent(event);
      break;
    case "channel.hidden":
      removeChannelFromEvent(event);
      break;
    case "notification.channel_mutes_updated":
      updateChannelMutesFromEvent(event);
      break;
    case "notification.removed_from_channel": // current user was removed from a channel
      updateLoadedChannel(event.channel.id, { unwatch: true });
      break;
    case "user.presence.changed":
      updateUser(flattenRawUser(event.user));
      break;
    case "typing.start":
      upsertUserTypingByChannel(event.channel_id, flattenRawUser(event.user));
      updateLoadedChannel(event.channel_id);
      break;
    case "typing.stop":
      removeUserTypingByChannel(event.channel_id, flattenRawUser(event.user));
      updateLoadedChannel(event.channel_id);
      break;
    default:
      break;
  }
}
